import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { PublicSiteHeader } from '@/components/PublicSiteHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, MapPin, CalendarDays, Mail, Phone, MessageCircle } from 'lucide-react';
import { rescuedAnimalsPublicApi } from '@/features/public/rescuedAnimalsPublicApi';
import { useLookupsSnapshot, toOptionLabel } from '@/features/lookups/snapshot';
import { FOUND_CONTEXT_OPTIONS } from '@/features/rescued-animals/types';
import { toAbsoluteUrl } from '@/lib/url';

interface RescuedAnimalDetailPageProps {
  forcedShelter?: { id: number; name?: string; slug?: string };
  idOverride?: string;
}

export default function RescuedAnimalDetailPage({ forcedShelter, idOverride }: RescuedAnimalDetailPageProps) {
  const { id: paramId } = useParams();
  const id = idOverride ?? paramId;
  const { data: snapshot } = useLookupsSnapshot();

  const { data: r, isLoading, isError } = useQuery({
    queryKey: ['public-rescued-animal', id],
    queryFn: () => rescuedAnimalsPublicApi.getById(Number(id)),
    enabled: !!id,
  });

  const backPath = forcedShelter?.slug ? `/${forcedShelter.slug}/encontrados` : '/encontrados';

  const speciesById = new Map<number, any>((snapshot?.species ?? []).map((s: any) => [s.id, s]));
  const sexById = new Map<number, any>((snapshot?.sexes ?? []).map((s: any) => [s.id, s]));
  const sizeById = new Map<number, any>((snapshot?.sizes ?? []).map((s: any) => [s.id, s]));
  const conditionById = new Map<number, any>((snapshot?.rescuedAnimalConditions ?? []).map((c: any) => [c.id, c]));

  return (
    <Layout showHeader={false} showFooter>
      <PublicSiteHeader />
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2">
          <Link to={backPath}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {forcedShelter ? `Volver a animales encontrados de ${forcedShelter.name || 'la entidad'}` : 'Volver'}
          </Link>
        </Button>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : isError || !r ? (
          <div className="text-center py-16 text-muted-foreground">
            Este registro ya no está disponible (puede haber expirado o haber sido reclamado).
          </div>
        ) : (
          <div className="space-y-6">
            {r.photos?.length > 0 && (
              <div className="relative w-full aspect-video bg-muted rounded-xl overflow-hidden">
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <img src={toAbsoluteUrl(r.photos[0].url)} className="w-full h-full object-contain bg-black/5" />
              </div>
            )}
            {r.photos?.length > 1 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {r.photos.slice(1).map((p: any) => (
                  <img key={p.id} src={toAbsoluteUrl(p.url)} alt="" className="w-full aspect-square object-cover rounded-lg" />
                ))}
              </div>
            )}

            <Card className="p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h1 className="text-2xl font-semibold">
                  {speciesById.get(r.speciesId) ? toOptionLabel(speciesById.get(r.speciesId)) : 'Animal encontrado'}
                </h1>
                <div className="flex gap-2 flex-wrap">
                  {sexById.get(r.sexId) && <span className="px-2 py-0.5 rounded-full border text-xs">{toOptionLabel(sexById.get(r.sexId))}</span>}
                  {sizeById.get(r.sizeId) && <span className="px-2 py-0.5 rounded-full border text-xs">{toOptionLabel(sizeById.get(r.sizeId))}</span>}
                  {conditionById.get(r.conditionId) && <span className="px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-medium">{toOptionLabel(conditionById.get(r.conditionId))}</span>}
                </div>
              </div>

              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Encontrado en: {r.foundArea}
                </div>
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  Fecha de ingreso: {String(r.intakeDate).slice(0, 10)}
                </div>
                <div>
                  Contexto: {FOUND_CONTEXT_OPTIONS.find((o) => o.value === r.foundContext)?.label ?? r.foundContext}
                </div>
              </div>

              <div>
                <h2 className="font-medium mb-1">Descripción</h2>
                <p className="text-sm text-muted-foreground">{r.description}</p>
              </div>
            </Card>

            {r.shelter && (
              <Card className="p-6 bg-primary/5 space-y-3">
                <h2 className="font-medium">¿Reconoces a este animal?</h2>
                <p className="text-sm text-muted-foreground">
                  Contacta a la entidad <strong>{r.shelter.name}</strong> para iniciar el proceso de reclamación.
                  Se te pedirán pruebas de propiedad antes de la entrega.
                </p>
                <div className="flex flex-col gap-2 text-sm">
                  {r.shelter.contactEmail && (
                    <a href={`mailto:${r.shelter.contactEmail}`} className="flex items-center gap-2 hover:underline">
                      <Mail className="h-4 w-4" />
                      {r.shelter.contactEmail}
                    </a>
                  )}
                  {r.shelter.contactPhone && (
                    <a href={`tel:${r.shelter.contactPhone}`} className="flex items-center gap-2 hover:underline">
                      <Phone className="h-4 w-4" />
                      {r.shelter.contactPhone}
                    </a>
                  )}
                  {r.shelter.whatsapp && (
                    <a
                      href={`https://wa.me/${String(r.shelter.whatsapp).replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 hover:underline"
                    >
                      <MessageCircle className="h-4 w-4" />
                      WhatsApp: {r.shelter.whatsapp}
                    </a>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
